import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';

export default function PatentResultsPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const searchId = searchParams.get('id');
  const searchQuery = searchParams.get('query') || '';
  const productTitle = searchParams.get('productTitle');
  const productImageUrl = searchParams.get('productImageUrl');
  
  const [patents, setPatents] = useState([]);
  const [sortedPatents, setSortedPatents] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [patentImages, setPatentImages] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [sortBy, setSortBy] = useState('greedy');
  const resultsPerPage = 70;
  const loadingImagesRef = useRef(new Set());
  
  // Search functionality states
  const [inputMode, setInputMode] = useState('asin');
  const [asin, setAsin] = useState('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState('');

  const loadPatentImage = useCallback(async (patentId) => {
    if (loadingImagesRef.current.has(patentId)) return;
    loadingImagesRef.current.add(patentId);
    
    try {
      const { patentService } = await import('../../services/patentService');
      const imageUrl = await patentService.getPatentImage(patentId);
      
      setPatentImages(prev => ({
        ...prev,
        [patentId]: imageUrl || 'no-image'
      }));
    } catch (err) {
      // Silently handle 404 or other image errors - just show no-image state
      setPatentImages(prev => ({
        ...prev,
        [patentId]: 'no-image'
      }));
    }
  }, []);

  // Search functionality
  const extractAndValidateAsin = (input) => {
    const trimmedInput = input.trim();
    
    if (!trimmedInput) {
      return { valid: false, asin: null, error: 'Please enter an ASIN or Amazon URL' };
    }

    let asinValue = trimmedInput;

    if (trimmedInput.includes('amazon.') || trimmedInput.includes('amzn.')) {
      const patterns = [
        /\/dp\/([A-Z0-9]{10})/i,
        /\/gp\/product\/([A-Z0-9]{10})/i,
        /\/product\/([A-Z0-9]{10})/i,
        /[?&]asin=([A-Z0-9]{10})/i,
        /\/([A-Z0-9]{10})(?:[/?]|$)/i
      ];

      for (const pattern of patterns) {
        const match = trimmedInput.match(pattern);
        if (match && match[1]) {
          asinValue = match[1];
          break;
        }
      }
    }

    const asinPattern = /^[A-Z0-9]{10}$/i;
    if (asinPattern.test(asinValue)) {
      return { valid: true, asin: asinValue.toUpperCase(), error: null };
    }

    return { valid: false, asin: null, error: 'Invalid ASIN format. ASIN should be 10 alphanumeric characters.' };
  };

  const handleNewSearch = async () => {
    setSearchError('');

    let validatedAsin = null;
    if (inputMode === 'asin') {
      const validation = extractAndValidateAsin(asin);
      if (!validation.valid) {
        setSearchError(validation.error);
        return;
      }
      validatedAsin = validation.asin;
      setAsin(validatedAsin);
    }

    if (inputMode === 'manual' && !title.trim()) {
      setSearchError('Please enter a product title');
      return;
    }

    setIsSearching(true);

    try {
      const { patentService } = await import('../../services/patentService');
      
      let searchParams;
      let queryText;

      if (inputMode === 'asin') {
        searchParams = { 
          asin: validatedAsin, 
          topK: 500,
          useAiRewrite: true 
        };
        queryText = `ASIN: ${validatedAsin}`;
      } else if (inputMode === 'manual') {
        searchParams = { 
          title: title.trim(), 
          description: description.trim(), 
          topK: 500,
          useAiRewrite: true 
        };
        queryText = title.trim() + (description.trim() ? ` - ${description.trim()}` : '');
      }

      const result = await patentService.search(searchParams);
      
      if (result.resultId) {
        const { searchHistoryUtil } = await import('../../utils/searchHistory');
        const historyEntry = {
          query: queryText,
          asin: inputMode === 'asin' ? validatedAsin : null,
          title: inputMode === 'manual' ? title.trim() : null,
          description: inputMode === 'manual' ? description.trim() : null,
          resultId: result.resultId,
        };
        
        if (result.productTitle) {
          historyEntry.productTitle = result.productTitle;
        }
        if (result.productImageUrl) {
          historyEntry.productImageUrl = result.productImageUrl;
        }
        
        searchHistoryUtil.addSearch(historyEntry);
        
        let navigateUrl = `/search?id=${result.resultId}&query=${encodeURIComponent(queryText)}`;
        if (result.productTitle) {
          navigateUrl += `&productTitle=${encodeURIComponent(result.productTitle)}`;
        }
        if (result.productImageUrl) {
          navigateUrl += `&productImageUrl=${encodeURIComponent(result.productImageUrl)}`;
        }
        
        navigate(navigateUrl);
        setIsSearching(false);
      } else {
        setSearchError('Search completed but no result ID returned');
        setIsSearching(false);
      }
    } catch (err) {
      setSearchError(err.message || 'Failed to initiate search. Please try again.');
      setIsSearching(false);
    }
  };

  useEffect(() => {
    if (!searchId) {
      navigate('/');
      return;
    }

    const fetchPatents = async () => {
      setIsLoading(true);
      setError('');

      try {
        const { patentService } = await import('../../services/patentService');
        
        let data;
        try {
          data = await patentService.getSearchResults(searchId);
        } catch (searchError) {
          data = await patentService.getReviewResult(searchId);
        }
        
        if (data.result && Array.isArray(data.result)) {
          const formattedPatents = data.result.map((patent) => ({
            id: patent.patent_id,
            patentNumber: patent.patent_id,
            title: patent.title,
            abstract: patent.abstract,
            claim: patent.claim,
            scores: {
              bm25: patent.bm25_score,
              cosine: patent.cosine_similarity,
              claimCosine: patent.claim_cosine_similarity,
              ranking: patent.greedy_ranking,
            },
            matchType: patent.match_type,
          }));
          
          setPatents(formattedPatents);
          setSortedPatents(formattedPatents);
        } else {
          setError('No patents found for this search');
        }
      } catch (err) {
        setError(err.message || 'Failed to fetch patent results. Please try again.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchPatents();
  }, [searchId, navigate]);

  useEffect(() => {
    if (sortedPatents.length === 0) return;
    
    const startIndex = (currentPage - 1) * resultsPerPage;
    const endIndex = startIndex + resultsPerPage;
    const currentPagePatents = sortedPatents.slice(startIndex, endIndex);
    
    currentPagePatents.forEach(patent => {
      loadPatentImage(patent.id);
    });
  }, [currentPage, sortedPatents, resultsPerPage, loadPatentImage]);

  useEffect(() => {
    if (patents.length === 0) return;
    
    let sorted = [...patents];
    
    switch (sortBy) {
      case 'cosine':
        sorted = sorted.filter(patent => patent.scores.cosine !== 0);
        sorted.sort((a, b) => a.scores.cosine - b.scores.cosine);
        break;
      case 'claim':
        sorted = sorted.filter(patent => patent.scores.claimCosine !== 0);
        sorted.sort((a, b) => a.scores.claimCosine - b.scores.claimCosine);
        break;
      case 'greedy':
        sorted = sorted.filter(patent => patent.scores.ranking !== 0);
        sorted.sort((a, b) => a.scores.ranking - b.scores.ranking);
        break;
      case 'original':
      default:
        break;
    }
    
    setSortedPatents(sorted);
    setCurrentPage(1);
  }, [sortBy, patents]);


  const totalPages = Math.ceil(sortedPatents.length / resultsPerPage);
  const startIndex = (currentPage - 1) * resultsPerPage;
  const endIndex = startIndex + resultsPerPage;
  const currentPatents = sortedPatents.slice(startIndex, endIndex);

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (isLoading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="text-center space-y-4">
          <svg className="animate-spin h-12 w-12 text-white mx-auto" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
          </svg>
          <p className="text-xl text-white font-semibold">Searching patents...</p>
          <p className="text-gray-300">This may take a few moments</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex-1 flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-red-500/90 text-white rounded-2xl p-8 space-y-4 text-center">
          <svg className="w-16 h-16 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          <p className="text-lg font-semibold">{error}</p>
          <button
            onClick={() => navigate('/')}
            className="px-6 py-2 bg-white text-red-600 rounded-xl font-semibold hover:bg-gray-100 transition-all"
          >
            Back to Search
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 px-4 py-8">
      <div className="max-w-7xl mx-auto space-y-6">
        {searchQuery && (
          <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20">
            <div className="flex items-start gap-4">
              {productImageUrl && (
                <div className="flex-shrink-0">
                  <img
                    src={productImageUrl}
                    alt="Product"
                    className="w-24 h-24 object-cover rounded-lg"
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                </div>
              )}
              <div className="flex-1">
                <h2 className="text-sm font-semibold text-gray-300 mb-1">Search Query</h2>
                <p className="text-white text-lg">{searchQuery}</p>
                {productTitle && (
                  <p className="text-gray-300 text-sm mt-2">
                    Product: <span className="text-white font-medium">{productTitle}</span>
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* New Search Form */}
        <div className="bg-white/10 backdrop-blur-sm rounded-xl shadow-xl p-6 space-y-4">
          <h2 className="text-xl font-bold text-white mb-4">Perform New Search</h2>
          
          {searchError && (
            <div className="bg-red-500/90 text-white border border-red-400 rounded-xl px-4 py-3 text-sm shadow-lg">
              {searchError}
            </div>
          )}

          <div className="flex gap-2">
            <button
              onClick={() => setInputMode('asin')}
              className={`py-2 px-4 rounded-lg text-sm font-medium transition-all ${
                inputMode === 'asin'
                  ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-lg'
                  : 'bg-white/10 text-gray-300 hover:bg-white/20'
              }`}
            >
              Search by ASIN
            </button>
            <button
              onClick={() => setInputMode('manual')}
              className={`py-2 px-4 rounded-lg text-sm font-medium transition-all ${
                inputMode === 'manual'
                  ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-lg'
                  : 'bg-white/10 text-gray-300 hover:bg-white/20'
              }`}
            >
              Describe Product
            </button>
          </div>

          <div className="space-y-4">
            {inputMode === 'asin' ? (
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-gray-200">
                  Amazon ASIN
                </label>
                <input
                  type="text"
                  value={asin}
                  onChange={(e) => setAsin(e.target.value)}
                  placeholder="e.g., B08N5WRWNW"
                  className="w-full px-4 py-3 rounded-xl bg-white/10 border border-white/20 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>
            ) : inputMode === 'manual' ? (
              <>
                <div className="space-y-2">
                  <label className="block text-sm font-semibold text-gray-200">
                    Product Title *
                  </label>
                  <input
                    type="text"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="Enter product title"
                    className="w-full px-4 py-3 rounded-xl bg-white/10 border border-white/20 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>
                <div className="space-y-2">
                  <label className="block text-sm font-semibold text-gray-200">
                    Product Description
                  </label>
                  <textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Enter detailed product description (optional)"
                    rows="3"
                    className="w-full px-4 py-3 rounded-xl bg-white/10 border border-white/20 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
                  />
                </div>
              </>
            ) : null}
          </div>

          <button
            onClick={handleNewSearch}
            disabled={isSearching}
            className="w-full py-3 px-6 rounded-xl font-bold text-white bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-500 hover:to-purple-500 shadow-lg transition-all hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0"
          >
            {isSearching ? (
              <span className="flex items-center justify-center gap-2">
                <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                Searching...
              </span>
            ) : (
              'Search Patents'
            )}
          </button>
        </div>

        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-3xl font-bold text-white">Patent Search Results</h1>
            <p className="text-gray-300 mt-1">
              Found {sortedPatents.length} relevant patents | Page {currentPage} of {totalPages}
            </p>
          </div>
        </div>

        <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20">
          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            <span className="text-white font-semibold whitespace-nowrap">Sort by:</span>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setSortBy('greedy')}
                className={`px-4 py-2 rounded-lg font-semibold transition-all text-sm ${
                  sortBy === 'greedy'
                    ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-lg'
                    : 'bg-white/10 hover:bg-white/20 text-white'
                }`}
              >
                Complete rank
              </button>
              <button
                onClick={() => setSortBy('cosine')}
                className={`px-4 py-2 rounded-lg font-semibold transition-all text-sm ${
                  sortBy === 'cosine'
                    ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-lg'
                    : 'bg-white/10 hover:bg-white/20 text-white'
                }`}
              >
                Title & Abstract Similarity
              </button>
              <button
                onClick={() => setSortBy('claim')}
                className={`px-4 py-2 rounded-lg font-semibold transition-all text-sm ${
                  sortBy === 'claim'
                    ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-lg'
                    : 'bg-white/10 hover:bg-white/20 text-white'
                }`}
              >
                Claim Similarity
              </button>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6 3xl:grid-cols-7 gap-3">
          {currentPatents.map((patent) => (
            <div
              key={patent.id}
              onClick={() => navigate(`/patent?id=${encodeURIComponent(patent.id)}&return=${encodeURIComponent(window.location.pathname + window.location.search)}`)}
              className="bg-white/10 backdrop-blur-sm rounded-xl overflow-hidden shadow-lg hover:shadow-2xl transition-all hover:-translate-y-1 flex flex-col cursor-pointer"
            >
              <div className="aspect-square bg-white relative overflow-hidden flex items-center justify-center">
                {patentImages[patent.id] && patentImages[patent.id] !== 'no-image' ? (
                  <img
                    src={patentImages[patent.id]}
                    alt={patent.title}
                    className="object-contain"
                    style={{ 
                      maxHeight: '100%', 
                      maxWidth: '100%', 
                      display: 'block'
                    }}
                  />
                ) : patentImages[patent.id] === 'no-image' ? (
                  <div className="w-full h-full flex items-center justify-center bg-gray-100">
                    <div className="text-center text-gray-400">
                      <svg className="w-8 h-8 mx-auto mb-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      <p className="text-xs">No image available</p>
                    </div>
                  </div>
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-gray-100">
                    <svg className="animate-spin h-6 w-6 text-gray-400" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                  </div>
                )}
              </div>
              
              <div className="p-3 flex-1 flex flex-col">
                <h3 className="text-white font-bold text-xs mb-2 line-clamp-3 flex-1">
                  {patent.title}
                </h3>
                
                <div className="mt-auto">
                  <span className="text-gray-400 text-[10px] font-mono">
                    {patent.patentNumber}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>

        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-2 mt-8">
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-lg font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white/10"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>

            <div className="flex gap-2">
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => {
                if (
                  page === 1 ||
                  page === totalPages ||
                  (page >= currentPage - 2 && page <= currentPage + 2)
                ) {
                  return (
                    <button
                      key={page}
                      onClick={() => handlePageChange(page)}
                      className={`px-4 py-2 rounded-lg font-semibold transition-all ${
                        currentPage === page
                          ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-lg'
                          : 'bg-white/10 hover:bg-white/20 text-white'
                      }`}
                    >
                      {page}
                    </button>
                  );
                } else if (
                  page === currentPage - 3 ||
                  page === currentPage + 3
                ) {
                  return (
                    <span key={page} className="px-2 py-2 text-white">
                      ...
                    </span>
                  );
                }
                return null;
              })}
            </div>

            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-lg font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white/10"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        )}
      </div>
    </div>
  );
}


